import React from "react";
import { formatCurrency } from "../../UI/DesignStandards";
import { yearStatementSections } from "../../../utility/financeYear";

const Count = ({ label, value, detail }) => (
  <div className="min-w-0">
    <div className="text-sm text-brand-muted break-words">{label}</div>
    <div className="mt-1 font-score text-2xl font-semibold text-brand-charcoal">
      {value}
    </div>
    {detail ? (
      <div className="text-sm text-brand-muted break-words">{detail}</div>
    ) : null}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="font-display font-bold text-brand-charcoal mb-1">{children}</h3>
);

const AmountRow = ({
  label,
  count,
  amount,
  strong,
  note,
  showCount,
  heading,
  indent,
}) => (
  <tr
    className={
      strong || heading
        ? "border-t border-brand-charcoal/20"
        : "border-t border-brand-charcoal/10"
    }
  >
    <th
      scope="row"
      className={`py-2 pr-3 text-left ${indent ? "pl-4" : ""} ${
        strong
          ? "font-semibold text-black"
          : heading
            ? "font-medium text-brand-charcoal"
            : "font-normal text-brand-muted"
      }`}
    >
      {label}
    </th>
    {showCount ? (
      <td
        className={`py-2 pl-4 text-right font-score whitespace-nowrap ${
          strong ? "font-semibold text-black" : "text-brand-charcoal"
        }`}
      >
        {count}
      </td>
    ) : null}
    <td
      className={`py-2 pl-4 text-right font-score whitespace-nowrap ${
        strong ? "text-lg font-semibold text-black" : "text-brand-charcoal"
      }`}
    >
      {amount == null ? null : formatCurrency(amount)}
      {note ? (
        <span className="block text-sm font-normal text-brand-muted">
          {note}
        </span>
      ) : null}
    </td>
  </tr>
);

const YearFinanceBlock = ({ year, rollup, statement, finance, loading }) => {
  if (loading) {
    return (
      <section className="card-board p-4 sm:p-5 min-w-0 max-w-full" aria-busy="true">
        <h2 className="font-display font-bold text-lg text-brand-charcoal mb-4">
          {year}
        </h2>
        <div className="grid grid-cols-3 gap-3">
          <Count label="Tournaments" value="…" />
          <Count label="Entries" value="…" />
          <Count label="Unique bowlers" value="…" />
        </div>
      </section>
    );
  }

  if (!rollup || !statement) return null;

  const sections = yearStatementSections({ rollup, statement, finance });

  return (
    <section className="card-board p-4 sm:p-5 min-w-0 max-w-full">
      <h2 className="font-display font-bold text-lg text-brand-charcoal mb-4">
        {year}
      </h2>
      <div className="grid grid-cols-3 gap-3">
        <Count label="Tournaments" value={sections.tournaments} />
        <Count
          label="Entries"
          value={sections.entries}
          detail={`${sections.onlineEntries} online · ${sections.walkUpEntries} walk-up`}
        />
        <Count label="Unique bowlers" value={sections.uniqueBowlers} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6 text-sm">
        <div className="min-w-0">
          <SectionTitle>Payments</SectionTitle>
          <div className="max-w-full overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th scope="col" className="sr-only">
                  Payment
                </th>
                <th
                  scope="col"
                  className="pb-1 pl-4 text-right font-normal text-brand-muted"
                >
                  Count
                </th>
                <th
                  scope="col"
                  className="pb-1 pl-4 text-right font-normal text-brand-muted"
                >
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {sections.channels.map((channel) => (
                <React.Fragment key={channel.label}>
                  <AmountRow
                    label={channel.label}
                    count={channel.count}
                    amount={channel.amount}
                    showCount
                    heading
                  />
                  {channel.rows.map((row) => (
                    <AmountRow
                      key={row.label}
                      label={
                        row.note ? (
                          <>
                            {row.label}
                            <sup className="ml-0.5">
                              <a
                                href="#online-card-note"
                                className="text-inherit no-underline"
                              >
                                {row.note}
                              </a>
                            </sup>
                          </>
                        ) : (
                          row.label
                        )
                      }
                      count={row.count}
                      amount={row.amount}
                      showCount
                      indent
                    />
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
          <p id="online-card-note" className="mt-3 text-xs text-brand-muted">
            <sup className="mr-0.5">1</sup>
            Online signup total based on Stripe’s estimated fees. See your
            Stripe account for how actual fees may have affected the deposit.
          </p>
        </div>
        <div className="min-w-0">
          <SectionTitle>Income</SectionTitle>
          <div className="max-w-full overflow-x-auto">
          <table className="w-full">
            <tbody>
              {sections.income.map((row) => (
                <AmountRow
                  key={row.label}
                  label={row.label}
                  amount={row.amount}
                />
              ))}
              <AmountRow label="Income" amount={sections.incomeTotal} strong />
            </tbody>
          </table>
          </div>
        </div>
        <div className="min-w-0">
          <SectionTitle>Allocated</SectionTitle>
          <div className="max-w-full overflow-x-auto">
          <table className="w-full">
            <tbody>
              {sections.allocated.map((row, index) => (
                <AmountRow
                  key={`${row.label}-${index}`}
                  label={row.label}
                  amount={row.amount}
                  note={
                    row.lockedPayouts != null
                      ? `Locked payouts ${formatCurrency(row.lockedPayouts)}`
                      : null
                  }
                />
              ))}
              <AmountRow
                label="Allocated"
                amount={sections.allocatedTotal}
                strong
              />
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default YearFinanceBlock;
